import React, { useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableHead from "@material-ui/core/TableHead";
import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import Collapse from "@material-ui/core/Collapse";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Switch from "@material-ui/core/Switch";
import InputAdornment from "@material-ui/core/InputAdornment";
import Radio from "@material-ui/core/Radio";
import RadioGroup from "@material-ui/core/RadioGroup";
import FormControl from "@material-ui/core/FormControl";
import FormLabel from "@material-ui/core/FormLabel";
import EditIcon from "@material-ui/icons/Edit";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import ReplayIcon from "@material-ui/icons/Replay";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import VisibilityIcon from "@material-ui/icons/Visibility";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import { useDialogStyles } from "../../theme/redesign";
import ConfirmationModal from "../../components/ConfirmationModal";
import {
  RecordCard,
  RecordCardMeta,
  RecordsCardsGrid,
  useViewMode,
  ViewModeToggle,
} from "../../components/ViewModeToggle";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import {
  accountIsAutopay,
  accountIsNonRenewable,
  accountLiveStatus,
  accountOccupancy,
  accountUsesPin,
  addDaysIso,
  daysLeft,
  flagEmoji,
  formatDate,
  matchesQuery,
  money,
  parseAccountCsv,
  readQuery,
  storedPhone,
  todayIso,
  withSuggestedSalePrice,
} from "./helpers";
import { DialogClose, EmptyHint, FilterChips, HintTip, LoadingHint, NegocioPager, PageFrame, PhoneFields, RelatedLinks, SearchField, StatusChip, DueBadge, stopRow, tNegocio, useMobile, usePager } from "./shared";
import { AccountIdentity, AccountLiveStatus, AccountPeek, ProductMark, ProfileEditDialog, ProfileIcons } from "./AccountPeek";
import {
  AccountDurationFields,
  AccountLicenseFields,
  AccountMailboxFields,
  AccountMoneyFields,
  AccountOriginFields,
  AccountPinFields,
  BillingTypePick,
  DualMoneyField,
  DURATION_PRESETS,
  accountFormExtras,
  applyBillingType,
  buildAccountPayload,
} from "./AccountFields";
import {
  COUNTRY_OPTIONS,
  hasNoValidity,
  isMailbox,
  needsSlots,
  parseEmailLot,
  productFilterLabel,
  saleTypeKey,
  slotKindOf,
  slotLabel,
  slotMaxLabel,
  usesGeneratedCode,
  usesLoginPanel,
  usesPinSlots,
  usesSiteLoginSlots,
} from "./catalog";
import { bumpNegocio, useRecords } from "./Lists";
import { useStyles } from "./styles";

const emptyAccount = (product) => {
  const startDate = todayIso();
  const noValidity = hasNoValidity(product);
  const durationDays = noValidity ? 0 : 30;
  const panel = usesGeneratedCode(product);
  const siteLogin = usesSiteLoginSlots(product);
  const device = slotKindOf(product) === "device";
  return {
    productId: product?.id || "",
    status: "available",
    botPublish: true,
    saleType: slotKindOf(product) === "none" && !panel ? "full" : "profile",
    email: "",
    password: "",
    url: "",
    pinEnabled: usesPinSlots(product),
    pinLength: 4,
    pinCustomEnabled: false,
    pinCustom: "",
    customProfileName: false,
    profileNamePrefix: "",
    billingType: "cost",
    isRenewable: true,
    originCountry: "",
    deviceLimit: device ? product?.deviceLimit || product?.maxProfiles || "" : "",
    lotSize: 1,
    emailsBulk: "",
    slotKind: slotKindOf(product),
    fulfillmentType: product?.fulfillmentType || "credentials",
    loginPanel: !!(product?.loginPanel || usesLoginPanel(product)),
    slotCount: panel || siteLogin ? product?.maxProfiles || 10 : product?.maxProfiles || "",
    startDate,
    durationDays,
    endDate: noValidity ? "" : addDaysIso(startDate, durationDays),
    currency: product?.suggestedCurrency === "fx" ? "fx" : "local",
    saleCurrency: product?.suggestedCurrency === "fx" ? "fx" : "local",
    costCurrency: "local",
    salePrice: Number(product?.suggestedPrice) > 0 ? product.suggestedPrice : "",
    salePriceFx: Number(product?.suggestedPriceFx) > 0 ? product.suggestedPriceFx : "",
    cost: "",
    costFx: "",
    notes: "",
    supplierId: "",
    isNew: true,
  };
};

const dialogFromAccount = (a) => {
  const id = Number(a?.id);
  return {
    ...emptyAccount(a?.product),
    ...a,
    ...accountFormExtras(a),
    id: Number.isFinite(id) && id > 0 ? id : undefined,
    productId: a?.productId || a?.product?.id || "",
    password: "",
    isNew: false,
  };
};

export default function AccountsPage() {
  const classes = useStyles();
  const isMobile = useMobile();
  const dlg = useDialogStyles();
  const history = useHistory();
  const location = useLocation();
  const [viewMode, setViewMode] = useViewMode("negocio-accounts");
  const accountsList = useRecords("accounts");
  const productsList = useRecords("products");
  const suppliersList = useRecords("suppliers");
  const records = accountsList.records;
  const products = useMemo(() => productsList.records.slice().sort((a, b) =>
    String(productFilterLabel(a, tNegocio, money)).localeCompare(String(productFilterLabel(b, tNegocio, money)), undefined, { sensitivity: "base" })
  ), [productsList.records]);
  const suppliers = suppliersList.records;
  const loading = accountsList.loading || productsList.loading;
  const [query, setQuery] = useState("");
  const [kind, setKind] = useState("");
  const [productId, setProductId] = useState("");
  const [supplierId, setSupplierId] = useState("");
  const [country, setCountry] = useState("");
  const [openId, setOpenId] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [showPass, setShowPass] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [info, setInfo] = useState(null);
  const [peekId, setPeekId] = useState(null);
  const [profileEdit, setProfileEdit] = useState(null);
  const [renew, setRenew] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [updatePrompt, setUpdatePrompt] = useState(null);
  const [updateSending, setUpdateSending] = useState(false);
  const [accountAgain, setAccountAgain] = useState(null);
  const lastProductIdRef = React.useRef("");
  const openedNewKey = React.useRef("");
  const aliveRef = React.useRef(true);
  const [supplierDraft, setSupplierDraft] = useState(null);
  const [supplierSaving, setSupplierSaving] = useState(false);

  const NEW_SUPPLIER = "__new_supplier__";

  const saveSupplierInline = async () => {
    if (!supplierDraft) return;
    const name = String(supplierDraft.name || "").trim();
    if (!name) {
      toast.error(tNegocio("fields.name"));
      return;
    }
    setSupplierSaving(true);
    try {
      const payload = {
        name,
        phone: storedPhone(supplierDraft.phone),
        telegram: String(supplierDraft.telegram || "").replace(/^@/, ""),
        notes: supplierDraft.notes || "",
      };
      const { data } = await api.post("/negocio/suppliers", payload);
      await suppliersList.reload({ soft: true });
      patchDialog({ supplierId: data.id });
      setSupplierDraft(null);
      bumpNegocio("suppliers");
      toast.success(tNegocio("toastSaved"));
    } catch (e) {
      toastError(e);
    } finally {
      setSupplierSaving(false);
    }
  };

  React.useEffect(() => () => {
    aliveRef.current = false;
  }, []);

  React.useEffect(() => {
    const params = readQuery(location.search);
    if (params.productId) setProductId(params.productId);
    if (params.supplierId) setSupplierId(params.supplierId);
    if (params.country) setCountry(String(params.country).toUpperCase());
    if (params.kind && ["available", "occupied", "expiring", "expired", "fault"].includes(params.kind)) setKind(params.kind);
    if (params.accountId) setPeekId(Number(params.accountId));
    if ((params.new !== "1" && params.new !== "true") || !products.length) return;
    if (openedNewKey.current === location.search) return;
    openedNewKey.current = location.search;
    const product = params.productId
      ? products.find((p) => Number(p.id) === Number(params.productId)) || products[0]
      : products[0];
    setShowPass(false);
    setDialog((current) => (Number(current?.id) > 0 ? current : emptyAccount(product)));
    const next = new URLSearchParams(location.search);
    next.delete("new");
    const search = next.toString();
    history.replace({ pathname: location.pathname, search: search ? `?${search}` : "" });
  }, [location.search, location.pathname, products, history]);

  const reload = ({ soft = true } = {}) => Promise.all([
    accountsList.reload({ soft }), productsList.reload({ soft }), suppliersList.reload({ soft }),
  ]);




  React.useEffect(() => {
    if (!peekId || loading) return;
    const row = records.find((a) => Number(a.id) === Number(peekId));
    if (row) {
      setInfo(row);
      setOpenId(row.id);
    }
    setPeekId(null);
  }, [peekId, records, loading]);

  const rows = useMemo(() => {
    // Disponibles con más días primero; falla y más vencidas al final.
    const STATUS_RANK = { available: 0, sold: 1, expiring: 2, idle: 3, expired: 4, fault: 5 };
    return records
      .filter((a) => {
        if (productId && Number(a.productId) !== Number(productId)) return false;
        if (supplierId && Number(a.supplierId) !== Number(supplierId)) return false;
        if (country && String(a.originCountry || a.product?.originCountry || "").toUpperCase() !== country) return false;
        const live = accountLiveStatus(a);
        if (kind === "available" && live !== "available") return false;
        if (kind === "occupied" && live !== "sold") return false;
        if (kind === "expiring" && live !== "expiring") return false;
        if (kind === "expired" && live !== "expired") return false;
        if (kind === "nonrenewable" && !accountIsNonRenewable(a)) return false;
        if (kind === "nonrenewable-expired" && !(accountIsNonRenewable(a) && daysLeft(a.endDate) < 0)) return false;
        if (kind === "fault" && live !== "fault") return false;
        return matchesQuery(a, query, [a.email, a.product?.name, a.supplier?.name, a.password]);
      })
      .slice()
      .sort((a, b) => {
        const sa = STATUS_RANK[accountLiveStatus(a)] ?? 9;
        const sb = STATUS_RANK[accountLiveStatus(b)] ?? 9;
        if (sa !== sb) return sa - sb;
        const da = daysLeft(a.endDate);
        const db = daysLeft(b.endDate);
        if (da == null && db == null) return String(a.email || "").localeCompare(String(b.email || ""));
        if (da == null) return 1;
        if (db == null) return -1;
        // Más días vigentes primero; más días vencidos (más negativo) al final.
        if (da !== db) return db - da;
        return String(a.email || "").localeCompare(String(b.email || ""));
      });
  }, [records, query, kind, productId, supplierId, country]);
  const pager = usePager(rows);
  const countries = useMemo(() => {
    const codes = new Set();
    records.forEach((a) => {
      const code = String(a.originCountry || a.product?.originCountry || "").toUpperCase();
      if (code) codes.add(code);
    });
    COUNTRY_OPTIONS.forEach((row) => {
      if (codes.has(row.code)) codes.add(row.code);
    });
    return COUNTRY_OPTIONS.filter((row) => codes.has(row.code));
  }, [records]);

  const selectedProduct = products.find((p) => Number(p.id) === Number(dialog?.productId));
  const panelAccount = usesGeneratedCode(selectedProduct || dialog);
  const siteLoginAccount = usesSiteLoginSlots(selectedProduct || dialog);
  const slotCount = panelAccount || siteLoginAccount
    ? Number(dialog?.slotCount || selectedProduct?.maxProfiles || 10)
    : selectedProduct?.slotKind === "device"
      ? selectedProduct?.deviceLimit || selectedProduct?.maxProfiles || 3
      : Number(dialog?.slotCount || selectedProduct?.maxProfiles || 5);

  const patchDialog = (patch) => setDialog((d) => ({ ...d, ...patch }));

  const runImport = async () => {
    if (!importFile) {
      toast.error(tNegocio("pages.accounts.import.pickFile"));
      return;
    }
    if (importFile.size > 5 * 1024 * 1024) {
      toast.error(tNegocio("pages.accounts.import.maxSize"));
      return;
    }
    setImporting(true);
    try {
      const text = await importFile.text();
      const rows = parseAccountCsv(text);
      if (!rows.length) {
        toast.error(tNegocio("pages.accounts.import.empty"));
        return;
      }
      const { data } = await api.post("/negocio/accounts/import", { rows });
      toast.success(
        tNegocio("pages.accounts.import.done")
          .replace("{created}", String(data?.created || 0))
          .replace("{skipped}", String(data?.skipped || 0))
      );
      if (Array.isArray(data?.errors) && data.errors.length) {
        toast.warn(data.errors.slice(0, 3).join(" · "));
      }
      setImportOpen(false);
      setImportFile(null);
      await reload();
    } catch (e) {
      toastError(e);
    } finally {
      setImporting(false);
    }
  };

  const onProductChange = (id) => {
    const product = products.find((p) => Number(p.id) === Number(id));
    const noValidity = hasNoValidity(product);
    const panel = usesGeneratedCode(product);
    const durationDays = noValidity ? 0 : dialog?.durationDays || 30;
    const startDate = dialog?.startDate || todayIso();
    patchDialog({
      productId: id,
      durationDays,
      currency: "local",
      saleCurrency: dialog?.saleCurrency || "local",
      costCurrency: dialog?.costCurrency || "local",
      endDate: noValidity ? "" : addDaysIso(startDate, durationDays),
      saleType: slotKindOf(product) === "none" && !panel ? "full" : dialog?.saleType || "profile",
      originCountry: dialog?.originCountry || "",
      deviceLimit: slotKindOf(product) === "device" ? product?.deviceLimit ?? dialog?.deviceLimit ?? "" : "",
      lotSize: 1,
      slotKind: slotKindOf(product),
      fulfillmentType: product?.fulfillmentType || "credentials",
      loginPanel: !!(product?.loginPanel || usesLoginPanel(product)),
      slotCount: panel ? product?.maxProfiles || 10 : "",
      pinEnabled: usesPinSlots(product),
    });
  };

  const askUpdateNotify = async (accountId) => {
    const id = Number(accountId);
    if (!(id > 0)) return;
    try {
      const { data } = await api.get(`/negocio/accounts/${id}/update-recipients`);
      const records = data?.records || [];
      if (!records.length) return;
      setUpdatePrompt({ accountId: id, count: records.length, recipients: records });
    } catch (e) {
      /* sin destinatarios o error: no bloquear el guardado */
    }
  };

  const sendAccountUpdates = async () => {
    if (!updatePrompt?.accountId) return;
    setUpdateSending(true);
    try {
      const { data } = await api.post(`/negocio/accounts/${updatePrompt.accountId}/notify-update`);
      const sent = Number(data?.sent || 0);
      toast.success(
        tNegocio("pages.accounts.updateNotifyDone").replace("{sent}", String(sent)).replace("{total}", String(updatePrompt.count))
      );
      setUpdatePrompt(null);
    } catch (e) {
      toastError(e);
    } finally {
      setUpdateSending(false);
    }
  };

  const saveAccount = async () => {
    if (!dialog) return;
    if (!dialog.productId) {
      toast.error(tNegocio("fields.product"));
      return;
    }
    const lot = parseEmailLot(dialog.emailsBulk || "", dialog.password || "");
    if (!String(dialog.email || "").trim() && !lot.length) {
      toast.error(siteLoginAccount ? tNegocio("fields.hostingAccount") : usesLoginPanel(selectedProduct || dialog) ? tNegocio("fields.loginUser") : tNegocio("fields.email"));
      return;
    }
    if (dialog.isNew && !siteLoginAccount && !dialog.password && lot.some((unit) => !unit.password)) {
      toast.error(tNegocio("fields.password"));
      return;
    }
    if (dialog.saleType === "profile" && dialog.pinEnabled && dialog.pinCustomEnabled && !String(dialog.pinCustom || "").trim()) {
      toast.error(tNegocio("fields.pinCustom"));
      return;
    }
    if (dialog.isNew && dialog.saleType === "profile" && dialog.customProfileName && !String(dialog.profileNamePrefix || "").trim()) {
      toast.error(tNegocio("fields.profileNamePrefix"));
      return;
    }
    try {
      const payload = buildAccountPayload(dialog);
      if (dialog.password) payload.password = dialog.password;
      const accountId = Number(dialog.id);
      const editing = Number.isFinite(accountId) && accountId > 0;
      if (editing) await api.put(`/negocio/accounts/${accountId}`, payload);
      else await api.post("/negocio/accounts", payload);
      toast.success(tNegocio("toastSaved"));
      const productIdSaved = dialog.productId;
      const carry = {
        supplierId: dialog.supplierId,
        billingType: dialog.billingType,
        originCountry: dialog.originCountry,
        durationDays: dialog.durationDays,
        startDate: dialog.startDate,
        endDate: dialog.endDate,
        currency: dialog.currency,
        saleCurrency: dialog.saleCurrency,
        costCurrency: dialog.costCurrency,
        salePrice: dialog.salePrice,
        salePriceFx: dialog.salePriceFx,
        cost: dialog.cost,
        costFx: dialog.costFx,
        saleType: dialog.saleType,
        pinEnabled: dialog.pinEnabled,
        pinLength: dialog.pinLength,
        slotCount: dialog.slotCount,
        deviceLimit: dialog.deviceLimit,
      };
      if (productIdSaved) lastProductIdRef.current = productIdSaved;
      setDialog(null);
      setShowPass(false);
      bumpNegocio("accounts");
      await reload({ soft: true });
      if (editing) await askUpdateNotify(accountId);
      else if (productIdSaved) {
        window.setTimeout(() => {
          if (aliveRef.current) setAccountAgain({ productId: productIdSaved, carry });
        }, 400);
      }
    } catch (e) {
      toastError(e);
    }
  };

  const runConfirm = async () => {
    if (!confirm) return;
    try {
      await api.delete(`/negocio/accounts/${confirm.id}`);
      toast.success(tNegocio("toastSaved"));
      setConfirm(null);
      reload({ soft: true });
    } catch (e) {
      toastError(e);
    }
  };

  const openRenew = (a) => {
    if (accountIsNonRenewable(a)) return;
    const startDate = addDaysIso(a.endDate || todayIso(), 1);
    const durationDays = a.durationDays || 30;
    setRenew({
      id: a.id,
      email: a.email,
      product: a.product,
      startDate,
      durationDays,
      endDate: addDaysIso(startDate, durationDays),
      salePrice: a.salePrice || 0,
      salePriceFx: a.salePriceFx,
      saleCurrency: a.saleCurrency === "fx" ? "fx" : "local",
      cost: a.cost || 0,
      costFx: a.costFx,
      costCurrency: a.costCurrency === "fx" ? "fx" : "local",
    });
  };

  const saveRenew = async () => {
    if (!renew?.id) return;
    try {
      await api.put(`/negocio/accounts/${renew.id}`, {
        renew: true,
        startDate: renew.startDate,
        durationDays: renew.durationDays,
        saleCurrency: renew.saleCurrency || "local",
        salePrice: renew.salePrice,
        salePriceFx: renew.salePriceFx,
        costCurrency: renew.costCurrency || "local",
        cost: renew.cost,
        costFx: renew.costFx,
      });
      toast.success(tNegocio("toastSaved"));
      setRenew(null);
      reload();
    } catch (e) {
      toastError(e);
    }
  };

  const patchRenew = (patch) => setRenew((d) => {
    const next = { ...d, ...patch };
    if (patch.startDate != null || patch.durationDays != null) {
      next.endDate = addDaysIso(next.startDate, next.durationDays);
    }
    return next;
  });

  const renderActions = (a) => {
    const left = daysLeft(a.endDate);
    const canRenew = !accountIsNonRenewable(a) && !accountIsAutopay(a) && !hasNoValidity(a) && left != null && left <= 3;
    return (
    <div className={classes.actionCluster}>
      {canRenew ? (
      <Tooltip title={tNegocio("renew")}>
        <span>
          <IconButton
            size="small"
            onClick={stopRow(() => openRenew(a))}
            style={{ color: "#6a1b9a" }}
          >
            <ReplayIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      ) : null}
      <Tooltip title={tNegocio("edit")}>
        <IconButton
          size="small"
          color="primary"
          onClick={stopRow(() => {
            setShowPass(false);
            setDialog(dialogFromAccount(a));
          })}
        >
          <EditIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title={tNegocio("pages.products.remove")}>
        <IconButton size="small" onClick={stopRow(() => setConfirm(a))}>
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </div>
    );
  };

  return (
    <>
      <PageFrame
        title={tNegocio("pages.accounts.title")}
        subtitle={tNegocio("pages.accounts.subtitle")}
        lockBody
        actions={
          <>
            <ViewModeToggle value={viewMode} onChange={setViewMode} />
            <Button startIcon={<CloudUploadIcon />} onClick={() => { setImportFile(null); setImportOpen(true); }}>
              {tNegocio("pages.accounts.import.action")}
            </Button>
            <Button variant="contained" color="primary" onClick={() => {
              const preferred = productId || lastProductIdRef.current;
              const product = products.find((p) => Number(p.id) === Number(preferred)) || products[0];
              setShowPass(false);
              setDialog(emptyAccount(product));
            }}>
              {tNegocio("new.account")}
            </Button>
          </>
        }
      >
        <div className={classes.toolbar}>
          <FilterChips
            value={kind}
            onChange={setKind}
            options={[
              { value: "", label: tNegocio("filters.all") },
              { value: "available", label: tNegocio("pages.accounts.filters.available") },
              { value: "occupied", label: tNegocio("pages.accounts.filters.occupied") },
              { value: "expiring", label: tNegocio("pages.accounts.filters.expiring") },
              { value: "expired", label: tNegocio("pages.accounts.filters.expired") },
              { value: "nonrenewable", label: "No renovables" },
              { value: "nonrenewable-expired", label: "No renovables vencidas" },
              { value: "fault", label: tNegocio("pages.accounts.filters.fault") },
            ]}
          />
          <div className={classes.selectRow}>
            <TextField
              select
              size="small"
              variant="outlined"
              label={tNegocio("pages.accounts.filterService")}
              InputLabelProps={{ shrink: true }}
              value={productId}
              onChange={(e) => setProductId(e.target.value)}
              style={{ minWidth: 180 }}
            >
              <MenuItem value="">{tNegocio("pages.accounts.allProducts")}</MenuItem>
              {products.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                    <ProductMark product={p} size={22} circle />
                    {productFilterLabel(p, tNegocio, money)}
                  </span>
                </MenuItem>
              ))}
            </TextField>
            {suppliers.length ? (
              <TextField
                select
                size="small"
                variant="outlined"
                label={tNegocio("pages.accounts.filterSupplier")}
                InputLabelProps={{ shrink: true }}
                value={supplierId}
                onChange={(e) => setSupplierId(e.target.value)}
                style={{ minWidth: 180 }}
              >
                <MenuItem value="">{tNegocio("pages.accounts.allSuppliers")}</MenuItem>
                {suppliers.map((s) => (
                  <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                ))}
              </TextField>
            ) : null}
            {countries.length ? (
              <TextField
                select
                size="small"
                variant="outlined"
                label={tNegocio("pages.accounts.filterCountry")}
                InputLabelProps={{ shrink: true }}
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                style={{ minWidth: 160 }}
              >
                <MenuItem value="">{tNegocio("pages.accounts.allCountries")}</MenuItem>
                {countries.map((row) => (
                  <MenuItem key={row.code} value={row.code}>{flagEmoji(row.code)} {row.name}</MenuItem>
                ))}
              </TextField>
            ) : null}
            {isMobile ? null : <SearchField value={query} onChange={setQuery} />}
          </div>
          {isMobile ? <SearchField value={query} onChange={setQuery} /> : null}
        </div>

        {rows.length && viewMode === "cards" ? (
          <>
          <div className={classes.listPane}>
          <RecordsCardsGrid>
            {pager.slice.map((a) => (
              <RecordCard key={a.id} actions={renderActions(a)} onClick={() => setInfo(a)}>
                <div className={classes.productHead} style={{ alignItems: "flex-start" }}>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <AccountIdentity account={a} />
                  </div>
                  <AccountLiveStatus account={a} compact />
                </div>
                <div style={{ marginTop: 8 }}><ProfileIcons account={a} onSelect={(p) => setProfileEdit({ profile: p, account: a })} /></div>
                {formatDate(a.endDate) && formatDate(a.endDate) !== "—" ? (
                  <RecordCardMeta>
                    {tNegocio("cols.endDate")}: {formatDate(a.endDate)}
                  </RecordCardMeta>
                ) : null}
                {a.endDate ? <DueBadge endDate={a.endDate} status={accountLiveStatus(a)} /> : null}
              </RecordCard>
            ))}
          </RecordsCardsGrid>
          </div>
          <NegocioPager
            count={pager.count}
            page={pager.page}
            rowsPerPage={pager.rowsPerPage}
            onChangePage={pager.setPage}
            onChangeRowsPerPage={pager.setRowsPerPage}
          />
          </>
        ) : rows.length ? (
          <div className={`${classes.tableWrap} ${classes.tableWrapFill}`}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell />
                  <TableCell>#</TableCell>
                  <TableCell>{tNegocio("cols.status")}</TableCell>
                  <TableCell>{tNegocio("cols.account")}</TableCell>
                  <TableCell>{tNegocio("cols.slots")}</TableCell>
                  <TableCell>{tNegocio("cols.billing")}</TableCell>
                  <TableCell>{tNegocio("cols.remaining")}</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {pager.slice.map((a, index) => {
                  const open = openId === a.id;
                  const showPin = accountUsesPin(a);
                  return (
                    <React.Fragment key={a.id}>
                      <TableRow hover className={classes.clickableRow} onClick={() => setInfo(a)}>
                        <TableCell width={40}>
                          <IconButton size="small" onClick={stopRow(() => setOpenId(open ? null : a.id))}>
                            {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                        </TableCell>
                        <TableCell>{pager.offset + index + 1}</TableCell>
                        <TableCell>
                          <AccountLiveStatus account={a} />
                        </TableCell>
                        <TableCell>
                          <AccountIdentity account={a} />
                        </TableCell>
                        <TableCell><ProfileIcons account={a} onSelect={(p) => setProfileEdit({ profile: p, account: a })} /></TableCell>
                        <TableCell>{formatDate(a.endDate) === "—" ? "" : formatDate(a.endDate)}</TableCell>
                        <TableCell>{a.endDate ? <DueBadge endDate={a.endDate} status={accountLiveStatus(a)} /> : null}</TableCell>
                        <TableCell align="right" className={classes.stickyActions} onClick={(e) => e.stopPropagation()}>
                          {renderActions(a)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={8} style={{ padding: 0, border: 0 }}>
                          <Collapse in={open} timeout="auto" unmountOnExit>
                            <div className={classes.profileBox}>
                              {accountOccupancy(a).saleType === "full" ? (
                                tNegocio(isMailbox(a) ? "pages.accounts.fullHintMailbox" : "pages.accounts.fullHint")
                              ) : (
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>{slotLabel(a, tNegocio)}</TableCell>
                                      {showPin ? <TableCell>PIN</TableCell> : null}
                                      <TableCell>{tNegocio("cols.contact")}</TableCell>
                                      <TableCell>{tNegocio("cols.status")}</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {(a.profiles || []).map((p) => (
                                      <TableRow
                                        key={p.id}
                                        hover
                                        className={classes.clickableRow}
                                        onClick={stopRow(() => setProfileEdit({ profile: p, account: a }))}
                                      >
                                        <TableCell>{p.name}</TableCell>
                                        {showPin ? <TableCell>{p.pin || ""}</TableCell> : null}
                                        <TableCell>{p.contact?.name || ""}</TableCell>
                                        <TableCell>
                                          <StatusChip
                                            value={p.status === "assigned" ? "occupied" : p.status}
                                            prefix={p.status === "assigned" || p.status === "available" ? "negocioModule.pages.accounts.profileStatus." : "negocioModule.status."}
                                          />
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                    {!(a.profiles || []).length && (
                                      <TableRow>
                                        <TableCell colSpan={showPin ? 4 : 3}>{tNegocio("pages.accounts.noProfiles")}</TableCell>
                                      </TableRow>
                                    )}
                                  </TableBody>
                                </Table>
                              )}
                              <RelatedLinks
                                items={[
                                  a.productId
                                    ? { to: `/negocio/productos`, label: a.product?.name || tNegocio("related.products") }
                                    : null,
                                  a.supplierId
                                    ? { to: `/negocio/proveedores`, label: a.supplier?.name || tNegocio("fields.supplier") }
                                    : null,
                                ]}
                              />
                            </div>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
            <NegocioPager
              count={pager.count}
              page={pager.page}
              rowsPerPage={pager.rowsPerPage}
              onChangePage={pager.setPage}
              onChangeRowsPerPage={pager.setRowsPerPage}
            />
          </div>
        ) : loading ? (
          <LoadingHint />
        ) : (
          <EmptyHint
            title={tNegocio("pages.accounts.emptyTitle")}
            subtitle={tNegocio("pages.accounts.empty")}
          />
        )}
      </PageFrame>

      <Dialog open={!!dialog} onClose={() => setDialog(null)} maxWidth="sm" fullWidth scroll="paper" classes={{ paper: dlg.paper }}>
        <DialogClose onClose={() => setDialog(null)} />
        <DialogTitle>{Number(dialog?.id) > 0 ? tNegocio("edit") : tNegocio("new.account")}</DialogTitle>
        <DialogContent dividers>
          <TextField
            select
            fullWidth
            margin="dense"
            variant="outlined"
            label={tNegocio("fields.product")}
            value={dialog?.productId || ""}
            onChange={(e) => onProductChange(e.target.value)}
          >
            {products.map((p) => (
              <MenuItem key={p.id} value={p.id}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                  <ProductMark product={p} size={22} circle />
                  {productFilterLabel(p, tNegocio, money)}
                </span>
              </MenuItem>
            ))}
          </TextField>
          <FormControl component="fieldset" margin="dense" fullWidth>
            <FormLabel component="legend">{tNegocio("fields.accountStatus")}</FormLabel>
            <RadioGroup
              row
              value={dialog?.status || "available"}
              onChange={(e) => {
                const status = e.target.value;
                patchDialog({
                  status,
                  ...(status !== "available" ? { botPublish: false } : {}),
                });
              }}
            >
              <FormControlLabel
                value="available"
                disabled={accountLiveStatus(dialog) === "expired"}
                control={<Radio color="primary" />}
                label={tNegocio("status.available")}
              />
              <FormControlLabel value="fault" control={<Radio color="primary" />} label={tNegocio("status.fault")} />
              <FormControlLabel value="idle" control={<Radio color="primary" />} label={tNegocio("status.idle")} />
              {accountLiveStatus(dialog) === "expired" || dialog?.status === "expired" ? (
                <FormControlLabel value="expired" control={<Radio color="primary" />} label={tNegocio("status.expired")} />
              ) : null}
            </RadioGroup>
            {accountLiveStatus(dialog) === "expired" ? (
              <div className={classes.captionBlock}>{tNegocio("pages.accounts.expiredBlocksAvailable")}</div>
            ) : null}
          </FormControl>
          <FormControlLabel
            control={
              <Switch
                color="primary"
                checked={dialog?.status === "available" && dialog?.botPublish !== false}
                disabled={dialog?.status !== "available"}
                onChange={(e) => patchDialog({ botPublish: e.target.checked })}
              />
            }
            label={tNegocio("pages.accounts.botPublish")}
          />
          {dialog?.status !== "available" ? (
            <div className={classes.captionBlock} style={{ marginBottom: 8 }}>
              {tNegocio("pages.accounts.botPublishBlocked")}
            </div>
          ) : null}
          {needsSlots(selectedProduct || dialog) ? (
            <>
              <div className={classes.sectionTitle}>{tNegocio("fields.saleType")}</div>
              <div className={classes.typePick}>
                <div
                  className={`${classes.typeCard} ${dialog?.saleType === "profile" ? classes.typeCardOn : ""}`}
                  onClick={() => patchDialog(withSuggestedSalePrice(dialog, { saleType: "profile" }))}
                  role="button"
                  tabIndex={0}
                >
                  <strong style={{ display: "inline-flex", alignItems: "center" }}>
                    {tNegocio(`pages.accounts.saleTypes.${saleTypeKey(dialog)}`)}
                    <HintTip text={tNegocio(`pages.accounts.saleTypes.${saleTypeKey(dialog)}Hint`)} />
                  </strong>
                </div>
                <div
                  className={`${classes.typeCard} ${dialog?.saleType === "full" ? classes.typeCardOn : ""}`}
                  onClick={() => patchDialog(withSuggestedSalePrice(dialog, { saleType: "full" }))}
                  role="button"
                  tabIndex={0}
                >
                  <strong style={{ display: "inline-flex", alignItems: "center" }}>
                    {tNegocio("pages.accounts.saleTypes.full")}
                    <HintTip text={tNegocio("pages.accounts.saleTypes.fullHint")} />
                  </strong>
                </div>
              </div>
            </>
          ) : null}
          <TextField
            fullWidth
            margin="dense"
            variant="outlined"
            label={
              isMailbox(dialog)
                ? tNegocio("fields.mailboxEmail")
                : siteLoginAccount
                  ? tNegocio("fields.hostingAccount")
                  : usesLoginPanel(selectedProduct || dialog)
                    ? tNegocio("fields.loginUser")
                    : tNegocio("fields.email")
            }
            value={dialog?.email || ""}
            onChange={(e) => patchDialog({ email: e.target.value })}
            helperText={
              siteLoginAccount
                ? tNegocio("fields.hostingAccountHint")
                : usesLoginPanel(selectedProduct || dialog)
                  ? tNegocio("fields.loginUserHint")
                  : undefined
            }
          />
          {!siteLoginAccount ? (
          <TextField
            fullWidth
            margin="dense"
            variant="outlined"
            type={showPass ? "text" : "password"}
            label={tNegocio("fields.password")}
            value={dialog?.password || ""}
            onChange={(e) => patchDialog({ password: e.target.value })}
            helperText={Number(dialog?.id) > 0 ? tNegocio("fields.passwordKeep") : ""}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setShowPass((v) => !v)}>
                    {showPass ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          ) : (
            <div className={classes.captionBlock} style={{ marginBottom: 8 }}>
              {tNegocio("pages.accounts.siteLoginHint")}
            </div>
          )}
          {usesLoginPanel(selectedProduct || dialog) && !isMailbox(dialog) && !siteLoginAccount ? (
            <TextField
              fullWidth
              margin="dense"
              variant="outlined"
              label={tNegocio("fields.panelUrl")}
              value={dialog?.url || ""}
              onChange={(e) => patchDialog({ url: e.target.value })}
              helperText={tNegocio("fields.panelUrlHint")}
              placeholder="http://servidor:puerto"
            />
          ) : null}

          <AccountMailboxFields form={dialog} onChange={patchDialog} />
          <AccountLicenseFields form={dialog} onChange={patchDialog} />

          {needsSlots(selectedProduct || dialog) && dialog?.saleType === "profile" ? (
            <>
              <TextField
                fullWidth
                margin="dense"
                variant="outlined"
                type="number"
                label={slotMaxLabel(dialog, tNegocio)}
                value={slotCount}
                onChange={
                  panelAccount || siteLoginAccount
                    ? (e) => patchDialog({ slotCount: e.target.value })
                    : undefined
                }
                InputProps={panelAccount || siteLoginAccount ? undefined : { readOnly: true }}
                helperText={
                  panelAccount || siteLoginAccount
                    ? tNegocio("pages.accounts.slotsEditableHint") ||
                      "Al guardar se crean los puestos faltantes para venta y edición."
                    : tNegocio("pages.accounts.slotsFromProduct")
                }
              />
              <AccountPinFields form={dialog} onChange={patchDialog} showNames={!(Number(dialog?.id) > 0)} />
            </>
          ) : null}

          <AccountOriginFields form={dialog} onChange={patchDialog} />
          <BillingTypePick value={dialog?.billingType} onChange={(billingType) => patchDialog(applyBillingType(dialog, billingType))} />
          <AccountDurationFields form={dialog} onChange={patchDialog} />
          <AccountMoneyFields form={dialog} onChange={patchDialog} slotCount={slotCount} />
          <TextField
            select
            fullWidth
            margin="dense"
            variant="outlined"
            label={tNegocio("fields.supplier")}
            value={dialog?.supplierId || ""}
            onChange={(e) => {
              const v = e.target.value;
              if (v === NEW_SUPPLIER) {
                setSupplierDraft({ name: "", phone: "", telegram: "", notes: "" });
                return;
              }
              patchDialog({ supplierId: v });
            }}
          >
            <MenuItem value="">{tNegocio("fields.none")}</MenuItem>
            {suppliers.map((s) => (
              <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
            ))}
            <MenuItem value={NEW_SUPPLIER}>
              <em>+ {tNegocio("new.supplier")}</em>
            </MenuItem>
          </TextField>
          <TextField
            fullWidth
            margin="dense"
            variant="outlined"
            multiline
            rows={2}
            label={tNegocio("fields.notes")}
            value={dialog?.notes || ""}
            onChange={(e) => patchDialog({ notes: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button color="primary" variant="contained" onClick={saveAccount}>{tNegocio("save")}</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!supplierDraft}
        onClose={() => !supplierSaving && setSupplierDraft(null)}
        maxWidth="sm"
        fullWidth
        classes={{ paper: dlg.paper }}
      >
        <DialogClose onClose={() => !supplierSaving && setSupplierDraft(null)} disabled={supplierSaving} />
        <DialogTitle>{tNegocio("new.supplier")}</DialogTitle>
        <DialogContent dividers>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            variant="outlined"
            label={tNegocio("fields.name")}
            value={supplierDraft?.name || ""}
            onChange={(e) => setSupplierDraft((f) => ({ ...f, name: e.target.value }))}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                saveSupplierInline();
              }
            }}
          />
          <PhoneFields
            value={supplierDraft?.phone || ""}
            onChange={(phone) => setSupplierDraft((f) => ({ ...f, phone }))}
          />
          <TextField
            fullWidth
            margin="dense"
            variant="outlined"
            label={tNegocio("fields.telegram")}
            value={supplierDraft?.telegram || ""}
            onChange={(e) => setSupplierDraft((f) => ({ ...f, telegram: e.target.value }))}
            helperText={tNegocio("pages.customers.telegramHint")}
          />
          <TextField
            fullWidth
            margin="dense"
            variant="outlined"
            multiline
            rows={2}
            label={tNegocio("fields.notes")}
            value={supplierDraft?.notes || ""}
            onChange={(e) => setSupplierDraft((f) => ({ ...f, notes: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button color="primary" variant="contained" disabled={supplierSaving} onClick={saveSupplierInline}>
            {tNegocio("save")}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmationModal
        title={tNegocio("pages.products.addAnotherAccountTitle")}
        open={!!accountAgain}
        onClose={() => setAccountAgain(null)}
        onConfirm={() => {
          const product = products.find((p) => Number(p.id) === Number(accountAgain?.productId)) || products[0];
          const carry = accountAgain?.carry || {};
          const kept = Object.fromEntries(Object.entries(carry).filter(([, v]) => v !== undefined && v !== null));
          setAccountAgain(null);
          setShowPass(false);
          setDialog({ ...emptyAccount(product), ...kept, productId: product?.id || "" });
        }}
        confirmText={tNegocio("pages.products.addAnotherAccountYes")}
        cancelText={tNegocio("pages.products.addAnotherAccountNo")}
      >
        {tNegocio("pages.products.addAnotherAccountBody")}
      </ConfirmationModal>
      <AccountPeek
        open={!!info}
        account={info}
        onClose={() => setInfo(null)}
        onChanged={reload}
        onRenew={(a) => {
          setInfo(null);
          openRenew(a);
        }}
        onEdit={(a) => {
          setInfo(null);
          setShowPass(false);
          setDialog(dialogFromAccount(a));
        }}
        onAskUpdateNotify={askUpdateNotify}
      />
      <ProfileEditDialog
        profile={profileEdit?.profile}
        account={profileEdit?.account}
        onClose={() => setProfileEdit(null)}
        onSaved={() => {
          setProfileEdit(null);
          reload();
        }}
      />
      <Dialog open={!!renew} onClose={() => setRenew(null)} maxWidth="xs" fullWidth scroll="paper" classes={{ paper: dlg.paper }}>
        <DialogClose onClose={() => setRenew(null)} />
        <DialogTitle>
          {tNegocio("renew")} · {renew?.product?.name || tNegocio("pages.accounts.title")}
        </DialogTitle>
        <DialogContent dividers>
          <div className={classes.captionBlock} style={{ marginBottom: 8 }}>{renew?.email}</div>
          <TextField
            fullWidth
            margin="dense"
            variant="outlined"
            type="date"
            label={tNegocio("fields.startDate")}
            InputLabelProps={{ shrink: true }}
            value={renew?.startDate || ""}
            onChange={(e) => patchRenew({ startDate: e.target.value })}
          />
          <div className={classes.filters} style={{ marginBottom: 8 }}>
            {DURATION_PRESETS.map((days) => (
              <Button
                key={days}
                size="small"
                color={Number(renew?.durationDays) === days ? "primary" : "default"}
                variant={Number(renew?.durationDays) === days ? "contained" : "outlined"}
                onClick={() => patchRenew({ durationDays: days })}
              >
                {tNegocio(`pages.accounts.durations.${days}`)}
              </Button>
            ))}
          </div>
          <TextField
            fullWidth
            margin="dense"
            variant="outlined"
            type="number"
            label={tNegocio("fields.days")}
            value={renew?.durationDays ?? 30}
            onChange={(e) => patchRenew({ durationDays: +e.target.value })}
          />
          <TextField
            fullWidth
            margin="dense"
            variant="outlined"
            type="date"
            label={tNegocio("fields.endDate")}
            InputLabelProps={{ shrink: true }}
            value={renew?.endDate || ""}
            InputProps={{ readOnly: true }}
          />
          <DualMoneyField
            label={tNegocio("pages.accounts.purchaseCost")}
            hint={tNegocio("pages.accounts.purchaseCostHint")}
            localValue={renew?.cost}
            fxValue={renew?.costFx}
            currency={renew?.costCurrency || "local"}
            onChange={({ local, fx, currency }) => patchRenew({ cost: local, costFx: fx, costCurrency: currency })}
          />
          <DualMoneyField
            label={tNegocio("pages.accounts.salePrice")}
            hint={tNegocio("pages.accounts.salePriceHint")}
            localValue={renew?.salePrice}
            fxValue={renew?.salePriceFx}
            currency={renew?.saleCurrency || "local"}
            onChange={({ local, fx, currency }) => patchRenew({ salePrice: local, salePriceFx: fx, saleCurrency: currency })}
          />
        </DialogContent>
        <DialogActions>
          <Button color="primary" variant="contained" onClick={saveRenew}>{tNegocio("save")}</Button>
        </DialogActions>
      </Dialog>
      <ConfirmationModal title={tNegocio("confirmTitle")} open={!!confirm} onClose={() => setConfirm(null)} onConfirm={runConfirm}>
        {confirm?.email ? <strong>{confirm.email}</strong> : null}
        <div>{tNegocio("deleteAccountBody")}</div>
      </ConfirmationModal>
      <Dialog
        open={importOpen}
        onClose={() => !importing && setImportOpen(false)}
        maxWidth="sm"
        fullWidth
        classes={{ paper: dlg.paper }}
      >
        <DialogClose onClose={() => !importing && setImportOpen(false)} disabled={importing} />
        <DialogTitle>{tNegocio("pages.accounts.import.title")}</DialogTitle>
        <DialogContent dividers>
          <div className={classes.importHint}>
            <strong>{tNegocio("pages.accounts.import.formatTitle")}</strong>
            <code>{tNegocio("pages.accounts.import.formatSample")}</code>
            <ul>
              <li>{tNegocio("pages.accounts.import.ruleProduct")}</li>
              <li>{tNegocio("pages.accounts.import.ruleEmail")}</li>
              <li>{tNegocio("pages.accounts.import.rulePass")}</li>
              <li>{tNegocio("pages.accounts.import.ruleOptional")}</li>
              <li>{tNegocio("pages.accounts.import.ruleDupes")}</li>
              <li>{tNegocio("pages.accounts.import.ruleMax")}</li>
            </ul>
          </div>
          <label className={classes.importDrop}>
            <input
              type="file"
              accept=".csv,text/csv"
              hidden
              onChange={(e) => setImportFile(e.target.files?.[0] || null)}
            />
            <CloudUploadIcon />
            <div style={{ fontWeight: 700 }}>
              {importFile ? importFile.name : tNegocio("pages.accounts.import.pickFile")}
            </div>
            <div className={classes.captionBlock}>{tNegocio("pages.accounts.import.dropHint")}</div>
          </label>
        </DialogContent>
        <DialogActions>
          <Button
            color="primary"
            variant="contained"
            onClick={runImport}
            disabled={importing || !importFile}
          >
            {tNegocio("pages.accounts.import.action")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!updatePrompt} onClose={() => !updateSending && setUpdatePrompt(null)} maxWidth="xs" fullWidth scroll="paper" classes={{ paper: dlg.paper }}>
        <DialogTitle>{tNegocio("pages.accounts.updateNotifyTitle")}</DialogTitle>
        <DialogContent dividers>
          {tNegocio("pages.accounts.updateNotifyBody").replace("{count}", String(updatePrompt?.count || 0))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUpdatePrompt(null)} disabled={updateSending}>
            {tNegocio("pages.customers.sendSkip")}
          </Button>
          <Button color="primary" variant="contained" onClick={sendAccountUpdates} disabled={updateSending}>
            {tNegocio("pages.accounts.updateNotifySend")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
